import React, {FC, ReactNode, useState} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {isTeamOrGlobalNamespace} from '../../../../helpers/models/namespaceHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {TagCreatedHandler} from '../../../inboxes/workspaces/editor/context/workspacesEditorContext';
import {CreateTagDropdownContainer} from './createTagDropdownContainer';
import {TagTemplate} from './tagEditorBody';

const createWorkspacePrivateTagHeight = 383;

/*
 * Props.
 */

interface CreateWorkspaceTagDropdown extends DropdownProps {
  userNamespace: string;
  createTagAsync: (template: TagTemplate) => Promise<void | TagModel>;
  backButtonLabel: ReactNode;
  onBackButtonClick: Handler;
  hasPreventPinTeamTags: boolean;
  initialNamespace?: string;
  onTagCreated?: TagCreatedHandler;
  shouldHideShowInWorkspace?: boolean;
  isEditingWorkspace?: boolean;
  initialName?: string;
}

/*
 * Component.
 */

export const CreateWorkspaceTagDropdown: FC<CreateWorkspaceTagDropdown> = (props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [tagError, setTagError] = useState<CustomError>();

  const onCreateTag = async (tagTemplate: TagTemplate, hasPreventPinTeamTags: boolean) => {
    const tag = {
      ...tagTemplate,
      ...(hasPreventPinTeamTags && isTeamOrGlobalNamespace(tagTemplate.namespace)
        ? {showInWorkspaces: false}
        : {}),
    };

    setIsSaving(true);
    setTagError(undefined);

    try {
      const newTag = await props.createTagAsync(tag);

      if (props.onTagCreated && newTag) {
        // Displays the created tag in P1 edit mode and shows its parents if they're hidden.
        props.onTagCreated(newTag, tag.parentTags);
      }

      if (props.onRequestClose) {
        props.onRequestClose();
      }
    } catch (error) {
      if (!(error instanceof CustomError)) {
        throw error;
      }

      // In case of conflict with an existing tag, display an error.
      setIsSaving(false);
      setTagError(error);
    }
  };

  return (
    <CreateTagDropdownContainer
      {...props}
      namespace={props.userNamespace}
      resourceNamespace={props.userNamespace}
      title={
        props.hasPreventPinTeamTags ? (
          <Trans id="private-tag-create-title">Create private tag</Trans>
        ) : (
          <Trans id="tag-create-title">Create new tag</Trans>
        )
      }
      height={props.hasPreventPinTeamTags ? createWorkspacePrivateTagHeight : props.height}
      okButtonLabel={<Trans id="tag-create-ok">Create</Trans>}
      onOkClick={onCreateTag}
      isSaving={isSaving}
      tagError={tagError}
      isEditingWorkspace={props.isEditingWorkspace}
      shouldHideNamespaceSelector={props.hasPreventPinTeamTags ? true : undefined}
    />
  );
};
